/**
 * How an asset's check rows are worded on its Status tab.
 *
 * Every asset carries every check kind — that is the rule the fleet is built
 * on (a capability the driver lacks lands as `unavailable`, never as a missing
 * row), and nothing here changes it. What changes is how the `unavailable`
 * rows read. An NX camera (2026-10-20, Moorebank: 146 of them) is dialled by
 * nobody; only NX's own answers are known, and the seven checks that need a
 * capability the `nx-camera` driver does not declare each read "driver
 * nx-camera does not declare capability video_channels" — the engine's
 * sentence, shown to an operator seven times per camera.
 *
 * Those rows are FOLDED, never dropped: one line with a count, open to the
 * list. A check that is `unavailable` for a real reason ("recording is off
 * in NX", "no system drive reported") is not a gap in the driver and stays on
 * the screen as it was.
 *
 * ⚠️ The match is on the reason's SHAPE, written by `src/checks/run.ts`, and
 * the regular expression here is the only place the screen knows it. Change
 * the sentence there and the fold stops folding — visibly, which is the safe
 * way for it to fail.
 */

import type { DetailCheck } from './api.ts';

/** The sentence `src/checks/run.ts` writes when the driver cannot be asked. */
const CAPABILITY_GAP = /^driver \S+ does not declare capability \S+$/;

/** Driver ids whose checks are answered through NX rather than the camera. */
const NX_DRIVER = 'nx-camera';

/** Is this row "the driver cannot ask that", rather than an answer? */
export function isCapabilityGap(check: Pick<DetailCheck, 'outcome' | 'reason'>): boolean {
  return check.outcome === 'unavailable' && check.reason !== null && CAPABILITY_GAP.test(check.reason);
}

/** The rows to show as they are, and the rows to fold into one line, each in its order. */
export function splitChecks<T extends Pick<DetailCheck, 'outcome' | 'reason'>>(
  checks: readonly T[],
): { shown: T[]; folded: T[] } {
  const shown: T[] = [];
  const folded: T[] = [];
  for (const check of checks) (isCapabilityGap(check) ? folded : shown).push(check);
  return { shown, folded };
}

/** The one line that stands for the folded rows. */
export function foldedLine(count: number, driverId: string | null): string {
  const noun = count === 1 ? 'check' : 'checks';
  const through = driverId === NX_DRIVER ? 'through NX' : 'by this driver';
  return `${count} ${noun} can’t be answered ${through}`;
}

/**
 * The wording for an NX camera's `video_present`, in place of its badge.
 *
 * On an nx-camera that check never fails: a pass is written for every frame NX
 * hands over, and when NX stops handing them over the row goes stale rather
 * than red. A green tick there says "video is fine" when all it knows is "a
 * frame arrived at this time" — so say that instead. Null means "keep the
 * badge": every other camera's pass is a real pass, and every other outcome
 * is what it says.
 */
export function lastFrameLine(
  check: Pick<DetailCheck, 'check_key' | 'outcome' | 'last_checked'>,
  driverId: string | null,
  now: number = Date.now(),
): string | null {
  if (driverId !== NX_DRIVER || check.check_key !== 'video_present' || check.outcome !== 'pass') return null;
  return `last frame ${ageWords(now - new Date(check.last_checked).getTime())}`;
}

/** "14 min ago", in units an operator would say aloud. */
function ageWords(ms: number): string {
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} days ago`;
}
